(ns app.front.view.fee.set-paid-modal
  (:require [app.domain.party.model :as party-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui :as ui]
            [app.front.ui.radio :as ui-radio]
            [app.front.ui.rich-text :as ui-rt]
            [app.front.ui.select :as ui-select]
            [app.front.util :as futil]
            [app.front.view.fee.subs :as fee-subs]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.event :refer [<sub >evt]]
            [core.front.feature-flags :as ff]
            [core.front.icons :as i]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(rf/reg-sub ::payment-details
  :<- [::ws-subs/authority-group]
  :<- [::entities/context]
  (fn [[auth-group ctx] _]
    (->> (entities/find-entities ctx
                                 {:entity/type            :entity.type/payment-detail
                                  :domain/authority-group (:db/id auth-group)})
         (sort-by :payment-detail/name))))

(rf/reg-sub ::card-types
  :<- [::ws-subs/view-data]
  :-> :payment-card-types)

(rf/reg-sub ::payer-per-bill
  :<- [::entities/entities {:entity/type :entity.type/bill}]
  (fn [bills]
    (->> bills
         (filter :bill/payer)
         (map (juxt :db/id :bill/payer))
         (into {}))))

(rf/reg-sub ::state :-> ::state)

(rf/reg-sub ::selected-payer
  :<- [::state]
  :<- [::payer-per-bill]
  (fn [[db payers] [_ parties bill-id]]
    (or (::selected-payer db)
        (get payers bill-id)
        (p/find-first parties #(party-model/has-role? % :role/payer)))))

(defn set-fn [key]
  (fn [state [_ value]]
    (assoc state key value)))

(defn get-fn [key]
  (fn [state _] (key state)))

(rf/reg-event-db ::set-selected-payer
  (rf/path [::state])
  (set-fn ::selected-payer))

(rf/reg-sub ::selected-payment-detail
  :<- [::state]
  (get-fn ::selected-payment-detail))

(rf/reg-event-db ::set-selected-payment-detail
  (rf/path [::state])
  (set-fn ::selected-payment-detail))

(rf/reg-sub ::selected-transaction-id
  :<- [::state]
  (get-fn ::selected-transaction-id))

(rf/reg-event-db ::set-transaction-id
  (rf/path [::state])
  (set-fn ::selected-transaction-id))

(rf/reg-sub ::selected-card-type
  :<- [::state]
  :<- [::card-types]
  (fn [[state card-types] _]
    (let [val (::selected-card-type state)]
      (if (seq card-types)
        ;; only allow card type value if it is in list
        ((set card-types) val)
        val))))

(rf/reg-event-db ::set-card-type
  (rf/path [::state])
  (set-fn ::selected-card-type))

(rf/reg-sub ::selected-payment-method
  :<- [::state]
  (fn [db _]
    (::selected-payment-method db :payment-method/cash)))

(rf/reg-event-db ::set-selected-payment-method
  (rf/path [::state])
  (fn [state [_ payment-method]]
    (-> state
        (assoc ::selected-payment-method payment-method)
        (dissoc ::selected-payment-detail ::cheque-number))))

(rf/reg-sub ::selected-payment-date
  :<- [::state]
  (fn [db _]
    (::selected-payment-date db (js/Date.))))

(rf/reg-event-db ::set-selected-payment-date
  (rf/path [::state])
  (set-fn ::selected-payment-date))

(rf/reg-sub ::additional-payment-info
  :<- [::state]
  :-> ::additional-payment-info)

(rf/reg-event-db ::set-additional-payment-info
  (rf/path [::state])
  (set-fn ::additional-payment-info))

(rf/reg-sub ::cheque-number
  :<- [::state]
  (get-fn ::cheque-number))

(rf/reg-event-db ::set-cheque-number
  (rf/path [::state])
  (set-fn ::cheque-number))

(rf/reg-event-db ::clear-state (fn [db _] (dissoc db ::state)))

(rf/reg-sub ::opts
  (fn [[_ bill-id parties]]
    [(rf/subscribe [::selected-payer parties bill-id])
     (rf/subscribe [::selected-payment-detail])
     (rf/subscribe [::selected-transaction-id])
     (rf/subscribe [::selected-card-type])
     (rf/subscribe [::selected-payment-method])
     (rf/subscribe [::selected-payment-date])
     (rf/subscribe [::additional-payment-info])
     (rf/subscribe [::cheque-number])])
  (fn [[payer payment-detail tx-id card-type payment-method payment-date additional-payment-info cheque-number] [_ bill-id _]]
    (let [card? (= :payment-method/online-card payment-method)]
      (-> (util/mmap {:payer-party-id (:db/id payer)}
                     bill-id
                     payment-date
                     additional-payment-info)
          (p/assoc-if :payment-detail-id (when-not card? (:db/id payment-detail))
                      :transaction-id (when card? tx-id)
                      :card-type (when card? card-type)
                      :payment-method payment-method
                      :cheque-number (when (:payment-detail/cheque? payment-detail) cheque-number))))))

(rf/reg-sub ::can-submit?
  (fn [[_ bill-id parties]]
    [(rf/subscribe [::selected-payer parties bill-id])
     (rf/subscribe [::selected-payment-detail])
     (rf/subscribe [::selected-transaction-id])
     (rf/subscribe [::selected-card-type])
     (rf/subscribe [::selected-payment-method])
     (rf/subscribe [::cheque-number])])
  (fn [[payer payment-detail tx-id card-type payment-method cheque-number] _]
    (let [cheque-number-enabled? (<sub [::ff/enabled? :feature-flag.id/add-cheque-number])]
      (and
        payer
        (cond
          (= :payment-method/online-card payment-method)
          (and (not (str/blank? tx-id))
               (not (str/blank? card-type)))
          (and cheque-number-enabled? (:payment-detail/cheque? payment-detail))
          (or (str/blank? cheque-number)
              (re-matches #"\d+" cheque-number))
          :else (some? payment-detail))))))

(rf/reg-event-fx ::set-paid
  (fn [_ [_ fsm-target opts]]
    (let [opts-nils-removed (->> opts
                                 (remove #(util/nil-or-blank? (second %)))
                                 (into {}))]
      {:dispatch-n [[::process/signal
                     fsm-target
                     [:process.signal/set-paid opts-nils-removed]
                     {:on-success [::clear-state]}]]})))

(rf/reg-sub ::payment-method-types
  (fn [[_ authority-group-id]]
    (rf/subscribe [::fee-subs/payment-methods authority-group-id]))
  (fn [payment-methods _]
    (->> payment-methods
         (remove :payment-method/disabled?)
         (map :payment-method/type)
         ;; OTC method first
         (sort-by (partial not= :payment-method/cash)))))

(defmulti payment-details (fn [method] method))
(defmethod payment-details :default [_] nil)

(defmethod payment-details :payment-method/cash
  [_]
  (let [tr (<sub [:tr])]
    [:div
     [ui-select/select
      {:id           "payment-detail"
       :data-cy      "payment-detail"
       :label-text   (tr [:fees/payment-method])
       :select-text  (tr [:fees/select-payment-method])
       :required     true
       :allow-empty? false
       :val->str     :payment-detail/name
       :value        (<sub [::selected-payment-detail])
       :tr           tr
       :on-change    (fn [detail]
                       (>evt [::set-selected-payment-detail detail]))
       :values       (<sub [::payment-details])}]]))

(defmethod payment-details :payment-method/online-card
  [_]
  (let [tr              (<sub [:tr])
        card-types      (<sub [::card-types])
        card-types-opts {:label-text (tr [:fees/card-type])
                         :required   true
                         :value      (<sub [::selected-card-type])}]
    [:div
     [ui/help (tr [:payment/set-online-card-paid-help])]
     [ui/help (tr [:payment/online-card-tx-id-note])]
     [ui/text-input
      {:label-text (tr [:fees/transaction-id])
       :required   true
       :on-change  #(rf/dispatch-sync [::set-transaction-id (futil/tval %)])
       :value      (<sub [::selected-transaction-id])}]
     [:div.mt-2
      (if (seq card-types)
        [ui-select/select
         (merge card-types-opts
                {:id           "select-card-type"
                 :allow-empty? false
                 :values       card-types
                 :on-change    #(>evt [::set-card-type %])})]
        [ui/text-input
         (merge card-types-opts
                {:on-change #(rf/dispatch-sync [::set-card-type (futil/tval %)])})])]]))

(defn set-paid-modal [{:keys [parties payment-method-types bill-id fsm-target]}]
  (let [tr                     (<sub [:tr])
        lang                   (<sub [:language])
        selected-method        (<sub [::selected-payment-method])
        selected-detail        (<sub [::selected-payment-detail])
        cheque-number-enabled? (<sub [::ff/enabled? :feature-flag.id/add-cheque-number])]
    [:div {:style {:min-height "410px"}}
     [:div.flex
      [:div
       [ui-select/select
        {:id           "payer-party"
         :label-text   (tr [:fees/payer-name-in-receipt])
         :required     true
         :allow-empty? false
         :val->str     #(party-model/full-name-with-roles lang %)
         :value        (<sub [::selected-payer parties bill-id])
         :tr           tr
         :on-change    (fn [payer]
                         (>evt [::set-selected-payer payer]))
         :values       parties}]
       [:div.mt-4
        (when (-> payment-method-types count (> 1))
          [ui-radio/radio
           {:label-text (tr [:payment/payment-method-select-label])
            :name       "payment-method-type"
            :values     payment-method-types
            :val->str   #(tr [%])
            :value      selected-method
            :on-change  #(>evt [::set-selected-payment-method %])}])]]
      [:div.mx-4
       [ui/date-picker {:label-text (tr [:payment/reported-payment-date])
                        :callback   (fn [date]
                                      (>evt [::set-selected-payment-date date]))
                        :max-date   (js/Date.)
                        :value      (js/Date.)}]]]
     [:div.mt-4
      [payment-details selected-method]]
     (when cheque-number-enabled?
       [:div.mt-4
        (when (:payment-detail/cheque? selected-detail)
          [ui/text-input
           {:id          "cheque-number"
            :label-text  (tr [:payment/cheque-number])
            :value       (<sub [::cheque-number])
            :input-class "w-56"
            :max-length  20
            :on-change   #(rf/dispatch-sync [::set-cheque-number (futil/tval %)])}])])
     [:div.mt-4
      [ui-rt/editor-legacy {:id                 "additional-payment-information"
                            :label-text         (tr [:payment/additional-information])
                            :authority-group-id (-> fsm-target
                                                    :entity/parent
                                                    :domain/authority-group
                                                    :db/id)
                            :initial-value      (<sub [::additional-payment-info])
                            :on-change          #(rf/dispatch-sync [::set-additional-payment-info %])
                            :tr                 tr}]]]))

(defn set-paid-button [fsm-target bill-id]
  (let [tr                   (<sub [:tr])
        parties              (->> fsm-target
                                  :entity/_parent
                                  (filter party-model/is-party?))
        payment-method-types (<sub [::payment-method-types (-> fsm-target
                                                               :entity/parent
                                                               :domain/authority-group
                                                               :db/id)])]
    [:div.bill-set-paid.ml-3.my-1
     [ui/modal-confirmation-button
      {:id                  "set-paid"
       :on-modal-open       #(>evt [::clear-state])
       :modal-title         (tr [:fees/payment-information])
       :tr                  tr
       :trigger-button-opts {:size    :small
                             :icon    i/check
                             :data-cy "set-paid"}
       :trigger-button-text (tr [:set-paid])
       :yes-disabled?       (not (<sub [::can-submit? bill-id parties]))
       :on-yes-click        (let [modal-opts (<sub [::opts bill-id parties])]
                              #(>evt [::set-paid fsm-target modal-opts]))
       :no-button-text      (tr [:cancel])
       :yes-button-text     (tr [:set-paid])
       :disabled?           (not (<sub [:app.front.process/signal-permitted?
                                        fsm-target
                                        [:process.signal/set-paid {:bill-id bill-id}]]))}
      [set-paid-modal (util/mmap parties payment-method-types bill-id fsm-target)]]]))

(defn set-unpaid-button [fsm-target bill-id]
  (when (<sub [:app.front.process/signal-permitted?
               fsm-target
               [:process.signal/set-unpaid {:bill-id bill-id}]])
    (let [tr (<sub [:tr])]
      [:div.bill-set-paid.ml-3.my-1
       [ui/modal-confirmation-button
        {:id                  "set-unpaid"
         :modal-title         (tr [:fees/set-unpaid])
         :tr                  tr
         :trigger-button-opts {:size    :small
                               :icon    i/undo
                               :data-cy "set-unpaid"}
         :trigger-button-text (tr [:fees/set-unpaid])
         :on-yes-click        #(>evt [::process/signal
                                      fsm-target
                                      [:process.signal/set-unpaid
                                       {:bill-id bill-id}]
                                      {:notify {:success :fees/set-unpaid-success}}])
         :no-button-text      (tr [:cancel])
         :yes-button-text     (tr [:fees/set-unpaid])}
        [:div (tr [:fees/set-unpaid-confirmation])]]])))
